import type { SupabaseClient } from "@supabase/supabase-js";
import {
  createCheckoutSession,
  type CreateCheckoutSessionResult,
} from "@/features/payment/createCheckoutSessionClient";
import {
  devFreeGrantBundle,
  devFreeRecordTokenPack,
  type DevFreeGrantDeps,
} from "@/features/payment/devFreeGrant";
import {
  loadEntitlementSnapshot,
  type OwnerEntitlements,
} from "@/features/payment/entitlementService";
import {
  getTokenPackDefinition,
  isBundleEntitlementKey,
  isTokenPackId,
} from "@/features/payment/paymentDefinitions";
import type {
  BundleEntitlementKey,
  TokenPackId,
} from "@/features/payment/types";

// "unavailable" is the production-safe fallback: no payment provider is
// selected, so every checkout path stays closed instead of guessing a real one.
export type PurchaseMode = "unavailable" | "dev_free" | "stripe_checkout";

const DEFAULT_PURCHASE_MODE: PurchaseMode = "dev_free";

export const PRODUCTION_ALLOWED_PURCHASE_MODES: readonly PurchaseMode[] = [
  "stripe_checkout",
];

export function resolvePurchaseMode(
  raw: string | undefined = import.meta.env.VITE_PURCHASE_MODE,
  isProd: boolean = import.meta.env.PROD === true,
): PurchaseMode {
  const known = raw === "stripe_checkout" || raw === "dev_free" ? raw : null;
  if (isProd) {
    // Resolution runs at module evaluation on the boot critical path, so an
    // unset or non-production mode must close checkout rather than stop the app.
    return known !== null && PRODUCTION_ALLOWED_PURCHASE_MODES.includes(known)
      ? known
      : "unavailable";
  }
  return known ?? DEFAULT_PURCHASE_MODE;
}

export const purchaseMode: PurchaseMode = resolvePurchaseMode();

export function isRealMoneyCheckoutAvailable(mode: PurchaseMode): boolean {
  return mode !== "unavailable";
}

export type ShopItemId = BundleEntitlementKey | TokenPackId;

export type PurchaseResult =
  | {
      readonly ok: true;
      readonly kind: "bundle";
      readonly bundleKey: BundleEntitlementKey;
      readonly outcome: "granted" | "already-owned";
      readonly entitlements: OwnerEntitlements;
    }
  | {
      readonly ok: true;
      readonly kind: "tokenPack";
      readonly packId: TokenPackId;
      readonly tokenGrant: number;
      readonly entitlements: OwnerEntitlements;
    }
  | {
      readonly ok: true;
      readonly kind: "redirect";
      readonly url: string;
    }
  | {
      readonly ok: false;
      readonly reason: "unknown-item" | "checkout-failed" | "unsupported-mode";
    };

export type CreateCheckoutSessionFn = (params: {
  readonly itemId: string;
  readonly supabase: SupabaseClient;
}) => Promise<CreateCheckoutSessionResult>;

export interface ShopPurchaseDeps extends DevFreeGrantDeps {
  readonly mode?: PurchaseMode;
  readonly supabase?: SupabaseClient;
  readonly createCheckoutSession?: CreateCheckoutSessionFn;
}

export async function purchaseShopItem(
  itemId: string,
  deps: ShopPurchaseDeps,
): Promise<PurchaseResult> {
  const mode = deps.mode ?? purchaseMode;

  if (mode === "unavailable") {
    return { ok: false, reason: "unsupported-mode" };
  }

  if (mode === "stripe_checkout") {
    if (!isBundleEntitlementKey(itemId) && !isTokenPackId(itemId)) {
      return { ok: false, reason: "unknown-item" };
    }
    if (!deps.supabase) {
      return { ok: false, reason: "checkout-failed" };
    }
    const invoke = deps.createCheckoutSession ?? createCheckoutSession;
    const result = await invoke({ itemId, supabase: deps.supabase });
    if (!result.ok) {
      if (result.reason === "unknown-item") {
        return { ok: false, reason: "unknown-item" };
      }
      return { ok: false, reason: "checkout-failed" };
    }
    return { ok: true, kind: "redirect", url: result.url };
  }

  if (isBundleEntitlementKey(itemId)) {
    try {
      const outcome = await devFreeGrantBundle(itemId, deps);
      const entitlements = await loadEntitlementSnapshot(
        deps.ownerId,
        deps.entitlementRepository,
      );
      return {
        ok: true,
        kind: "bundle",
        bundleKey: itemId,
        outcome,
        entitlements,
      };
    } catch (error) {
      console.warn("dev_free bundle grant failed:", error);
      return { ok: false, reason: "checkout-failed" };
    }
  }

  if (isTokenPackId(itemId)) {
    try {
      await devFreeRecordTokenPack(itemId, deps);
      const entitlements = await loadEntitlementSnapshot(
        deps.ownerId,
        deps.entitlementRepository,
      );
      const definition = getTokenPackDefinition(itemId);
      return {
        ok: true,
        kind: "tokenPack",
        packId: itemId,
        tokenGrant: definition.tokenGrant,
        entitlements,
      };
    } catch (error) {
      console.warn("dev_free token pack record failed:", error);
      return { ok: false, reason: "checkout-failed" };
    }
  }

  return { ok: false, reason: "unknown-item" };
}
